import produce from 'immer'
import { assign, filter, forEach, get, isEmpty, map, set, uniqBy } from 'lodash'

import { METADATA_TYPES } from '../../../common/constants'
import { buildTreeHierarchy, transformTree } from '../../../common/helpers'

import {
  ADD_COURSE_TYPE,
  DELETE_COURSE_TYPE,
  EXPAND_COURSE_TYPE_MAIN_TREE_KEYS,
  EXPAND_COURSE_TYPE_SUPPORTING_TREE_KEYS,
  FETCH_COURSE_TYPES,
  MOVE_COURSE_TYPES,
  RENAME_COURSE_TYPE,
  REPLACE_COURSE_TYPE,
  RESET_COURSE_TYPE_TREE,
  REVERT_REPLACE_COURSE_TYPE,
  SAVE_COURSE_TYPES,
  SET_DATA_FOR_RENAMING_COURSE_TYPE,
  SET_IS_CURATION_ENABLED_COURSE_TYPE,
} from '../action-types'
import { COURSE_TYPES_INITIAL_STATE } from '../initial-states'

const courseTypesReducer = (state = COURSE_TYPES_INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_COURSE_TYPES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_COURSE_TYPES.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } = action.payload.data.courseTypes

        const treeData = buildTreeHierarchy(
          results,
          METADATA_TYPES.COURSE_TYPES,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          latestEvent,
        })
      })

    case FETCH_COURSE_TYPES.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_COURSE_TYPES.CLEANUP:
      return { ...COURSE_TYPES_INITIAL_STATE }

    case SAVE_COURSE_TYPES.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          latestEvent: undefined,
        }),
      )

    case SAVE_COURSE_TYPES.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } = action.payload.data.saveCourseTypes

        const treeData = buildTreeHierarchy(
          results,
          METADATA_TYPES.COURSE_TYPES,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
          latestEvent,
        })
      })

    case SAVE_COURSE_TYPES.FAIL:
      return produce(state, nextState => {
        const { graphQLErrors, message } = action.error

        const entities = get(graphQLErrors, '[0].extensions.exception.entities')

        return assign(nextState, {
          isLoading: false,
          error: {
            message,
            entities,
          },
          latestEvent: undefined,
        })
      })

    case DELETE_COURSE_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case DELETE_COURSE_TYPE.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.deleteCourseType

        const treeData = buildTreeHierarchy(
          results,
          METADATA_TYPES.COURSE_TYPES,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case DELETE_COURSE_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case REPLACE_COURSE_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case REPLACE_COURSE_TYPE.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.replaceCourseType

        const treeData = buildTreeHierarchy(
          results,
          METADATA_TYPES.COURSE_TYPES,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case REPLACE_COURSE_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case REVERT_REPLACE_COURSE_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case REVERT_REPLACE_COURSE_TYPE.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.revertReplaceCourseType

        const treeData = buildTreeHierarchy(
          results,
          METADATA_TYPES.COURSE_TYPES,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case REVERT_REPLACE_COURSE_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case SET_IS_CURATION_ENABLED_COURSE_TYPE.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case SET_IS_CURATION_ENABLED_COURSE_TYPE.COMPLETE:
      return produce(state, nextState => {
        const updatedCourseType =
          action.payload.data.setIsCurationEnabledCourseType

        const treeData = transformTree(nextState.initialData, node =>
          node.id === updatedCourseType.id
            ? {
                ...node,
                isCurationEnabled: updatedCourseType.isCurationEnabled,
              }
            : node,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case SET_IS_CURATION_ENABLED_COURSE_TYPE.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case ADD_COURSE_TYPE:
      return produce(state, nextState => {
        const { parentPath } = action.payload
        const isRootNodeAdded = isEmpty(parentPath)

        const arrayToAddNodeTo = isRootNodeAdded
          ? nextState.data
          : get(nextState.data, `${parentPath}.children`, [])

        arrayToAddNodeTo.push(action.payload)

        const treeDataWithNewNode = isRootNodeAdded
          ? arrayToAddNodeTo
          : set(nextState.data, `${parentPath}.children`, arrayToAddNodeTo)

        return assign(nextState, {
          ...nextState,
          data: treeDataWithNewNode,
          isDirty: true,
          dataForRenaming: { id: action.payload.id },
        })
      })

    case RENAME_COURSE_TYPE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: set(
            nextState.data,
            `${action.payload.path}.name`,
            action.payload.name,
          ),
          isDirty: true,
        }),
      )

    case SET_DATA_FOR_RENAMING_COURSE_TYPE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          dataForRenaming: action.payload,
        }),
      )

    case MOVE_COURSE_TYPES:
      return produce(state, nextState => {
        const { nodesToMove, destinationNode } = action.payload
        let newTreeData = nextState.data

        // remove nodes to be moved
        forEach(nodesToMove, ({ id: idToMove, parentPath }) => {
          const isMovingRootNode = isEmpty(parentPath)

          if (isMovingRootNode) {
            newTreeData = filter(newTreeData, ({ id }) => id !== idToMove)
          } else {
            newTreeData = set(
              newTreeData,
              `${parentPath}.children`,
              filter(
                get(newTreeData, `${parentPath}.children`),
                ({ id }) => id !== idToMove,
              ),
            )
          }
        })

        const isMovedToRoot = destinationNode.level === 1

        if (isMovedToRoot) {
          newTreeData = [
            ...newTreeData,
            ...map(nodesToMove, node => ({
              ...node,
              parentId: null,
              level: 1,
              idPath: [node.id],
              namePath: [node.name],
            })),
          ]
        } else {
          newTreeData = transformTree(newTreeData, node =>
            node.id === destinationNode.id
              ? {
                  ...node,
                  children: uniqBy(
                    [
                      ...(node.children || []),
                      ...map(nodesToMove, node => ({
                        ...node,
                        parentId: destinationNode.id,
                        level: destinationNode.level - 1,
                        idPath: [...destinationNode.idPath, node.id],
                        namePath: [...destinationNode.namePath, node.name],
                      })),
                    ],
                    'id',
                  ),
                }
              : node,
          )
        }

        return assign(nextState, {
          ...nextState,
          isDirty: true,
          data: buildTreeHierarchy(newTreeData, METADATA_TYPES.COURSE_TYPES),
        })
      })

    case EXPAND_COURSE_TYPE_MAIN_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          mainTreeExpandedKeys: action.payload,
        }),
      )

    case EXPAND_COURSE_TYPE_SUPPORTING_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          supportingTreeExpandedKeys: action.payload,
        }),
      )

    case RESET_COURSE_TYPE_TREE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          error: undefined,
          data: nextState.initialData,
          isDirty: false,
          dataForRenaming: undefined,
        }),
      )

    default:
      return state
  }
}

export default courseTypesReducer
